import { Box, Divider, TextField, IconButton, Chip, MenuItem, Typography, Select } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useMapThemeClasses } from "../../../../hooks/use-map-theme-styles";
import { ClearRounded as ClearIcon, Search as SearchIcon, Check } from "@material-ui/icons";
import { useEffect, useState } from "react";
import useStyles from "../../../../components/LyfeTable/use-styles";
import Tasks from "../tasks/Root";
import { useRecoilValue } from "recoil";
import { userPersonalInfoState } from "apps/platform-client/client/src/store/user";
import { TasksStatus, TasksTimeframe } from "apps/platform-client/client/src/models/enums/enums";
import {
	getBasicTaskFilter,
	getBasicUserFilter,
	setAssignToFilters,
	setStatusFilters,
	setTimeframeFilters,
	searchItem,
} from "./inbox-tasks-filter-service";
import { useDataMutation, useDataQuery } from "apps/platform-client/client/src/hooks/useDataModule";
import { GET_INBOX_NOTIFICATION_FILTERS, UPDATE_INBOX_NOTIFICATION_FILTERS } from "./queries";
import isJsonString from "apps/platform-client/client/src/utils/is-json-string";
import { useDebounce } from "@lyfegen-platform/react-common";
import { MS_UNTIL_QUERY_CALL } from "apps/platform-client/client/src/constants/util-constants";
import { NotificationsType } from "apps/platform-client/client/src/models/common/notifications";

interface TasksTabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

const TasksTabPanel: React.FC<TasksTabPanelProps> = (props: TasksTabPanelProps) => {
	const { value, index, ...other } = props;
	const { t } = useTranslation();
	const userData = useRecoilValue(userPersonalInfoState);
	const notificationIds = [
		NotificationsType.FormDueExact,
		NotificationsType.AgreementApproval,
		NotificationsType.RefundInvoiceReview,
		NotificationsType.RefundInvoiceResendForReview,
		NotificationsType.RefundInvoiceReceiverApproval,
		NotificationsType.RefundInvoiceReceiverRejection,
		NotificationsType.RefundInvoiceReceiverConfirmation,
		NotificationsType.RefundInvoiceSenderConfirmation,
		NotificationsType.RefundInvoiceSenderRejection,
		NotificationsType.NegotiableAttributeProposal,
	];
	const taskStatusOptions = [TasksStatus.AnyStatus, TasksStatus.Open, TasksStatus.Closed];
	const taskTimeframeOptions = [TasksTimeframe.AnyTime, TasksTimeframe.Today, TasksTimeframe.ThisWeek, TasksTimeframe.ThisMonth];
	const { search } = useMapThemeClasses();
	const [searchInput, setSearchInput] = useState("");
	const { assignedToMeChip, selectField, selectFieldTask, checkLabel } = useStyles({});
	const [tasksTimeFrameFilter, setTasksTimeframeFilter] = useState<TasksTimeframe>(TasksTimeframe.AnyTime);
	const [tasksStatusFilter, setTasksStatusFilter] = useState<TasksStatus>();
	const [assignedToMeFilter, setAssignedToMeFilter] = useState(false);
	const [userFilter, setUserFilter] = useState(getBasicUserFilter(userData.id, notificationIds));
	const [taskFilter, setTaskFilter] = useState(getBasicTaskFilter(notificationIds));
	const [statusFilter, setStatusFilter] = useState<string[]>([]);
	const [timeframeFilter, setTimeframeFilter] = useState<string[]>([TasksTimeframe.AnyTime]);
	const [updateInboxNotificationFilter] = useDataMutation(UPDATE_INBOX_NOTIFICATION_FILTERS);
	const { data, loading } = useDataQuery(GET_INBOX_NOTIFICATION_FILTERS, { fetchPolicy: "no-cache" });

	const debouncedTerm = useDebounce(searchInput, MS_UNTIL_QUERY_CALL);

	function clearInput() {
		setSearchInput("");
	}

	useEffect(() => {
		refreshFilters(assignedToMeFilter, tasksTimeFrameFilter, tasksStatusFilter, debouncedTerm || "");
	}, [debouncedTerm]);

	function handleSearchInput(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
		setSearchInput(e.target.value || "");
	}

	function handleStatusFilterOptions(e: any) {
		const {
			target: { value },
		} = e;
		const curValue = statusFilter.some((status) => status === value) ? [TasksStatus.AnyStatus] : [value];
		setStatusFilter(curValue);
		setTasksStatusFilter(curValue.length ? curValue[0] : undefined);
		refreshFilters(assignedToMeFilter, tasksTimeFrameFilter, curValue.length ? curValue[0] : undefined, searchInput);
		updateInboxNotificationFilter({
			variables: {
				req: JSON.stringify({
					inboxTasksTimeframe: tasksTimeFrameFilter || "",
					inboxTasksStatus: curValue.length ? curValue[0] : "",
					inboxTasksAssignedTo: assignedToMeFilter,
				}),
			},
		});
	}

	useEffect(() => {
		if (data) {
			const res = isJsonString(data.filters) ? JSON.parse(data.filters || "[]") : [];
			setTasksTimeframeFilter(res?.inboxTasksTimeframe);
			setTasksStatusFilter(res?.inboxTasksStatus);
			setAssignedToMeFilter(res?.inboxTasksAssignedTo);

			setStatusFilter([res?.inboxTasksStatus]);
			setTimeframeFilter([res?.inboxTasksTimeframe]);
			refreshFilters(res?.inboxTasksAssignedTo, res?.inboxTasksTimeframe, res?.inboxTasksStatus, searchInput);
		}
	}, [data]);

	function refreshFilters(assignToFilter: boolean, timeFrameF: any, statusFilter: any, searchValue: string) {
		const basicTaskFilter = getBasicTaskFilter(notificationIds);
		const basicUserFilter = getBasicUserFilter(userData.id, notificationIds);

		const taFilter = setAssignToFilters(assignToFilter, userData.id, basicTaskFilter);
		const timeTaskFil = setTimeframeFilters(timeFrameF, taFilter);
		const statusTaskFilter = setStatusFilters(statusFilter, timeTaskFil);
		const taskFilterSearch = searchItem(searchValue, statusTaskFilter, userData.preferredLanguage);

		setTaskFilter(taskFilterSearch);
		setUserFilter(basicUserFilter);
	}

	function handleTimeframeFilterOptions(e: any) {
		const {
			target: { value },
		} = e;
		const curValue = timeframeFilter.some((s) => s === value) ? [TasksTimeframe.AnyTime] : [value];
		setTimeframeFilter(curValue);
		setTasksTimeframeFilter(curValue.length ? curValue[0] : undefined);
		refreshFilters(assignedToMeFilter, curValue.length ? curValue[0] : undefined, tasksStatusFilter, searchInput);

		updateInboxNotificationFilter({
			variables: {
				req: JSON.stringify({
					inboxTasksTimeframe: curValue.length ? curValue[0] : "",
					inboxTasksStatus: tasksStatusFilter,
					inboxTasksAssignedTo: assignedToMeFilter,
				}),
			},
		});
	}

	function handleAssignedToMe() {
		setAssignedToMeFilter(!assignedToMeFilter);
		refreshFilters(!assignedToMeFilter, tasksTimeFrameFilter, tasksStatusFilter, searchInput);

		updateInboxNotificationFilter({
			variables: {
				req: JSON.stringify({
					inboxTasksTimeframe: tasksTimeFrameFilter,
					inboxTasksStatus: tasksStatusFilter,
					inboxTasksAssignedTo: !assignedToMeFilter,
				}),
			},
		});
	}

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			style={{ height: "100%" }}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box style={{ height: "100%" }}>
					<Box>
						<Box
							display="flex"
							alignItems="center"
							flexDirection={"row"}
							justifyContent="start"
							style={{
								gap: 16,
							}}
						>
							<TextField
								variant="outlined"
								InputProps={{
									classes: { root: search },
									startAdornment: <SearchIcon color="primary" />,
									endAdornment: searchInput ? (
										<IconButton onClick={clearInput}>
											<ClearIcon />
										</IconButton>
									) : (
										""
									),
								}}
								placeholder={` ${t("search")} ${t("tasks")}...`}
								value={searchInput}
								onChange={handleSearchInput}
							/>

							<Chip
								label={t("assigned-to-me")}
								icon={<Check className={checkLabel} style={{ display: assignedToMeFilter ? "inline-block" : "none" }} />}
								className={assignedToMeChip}
								component="a"
								variant="outlined"
								style={{
									borderColor: assignedToMeFilter ? "#367bf5" : "rgba(54, 123, 245, 0.4)",
								}}
								onClick={() => handleAssignedToMe()}
							/>
							<Select
								id="filter-timeframe"
								className={`${selectField} ${selectFieldTask}`}
								variant="outlined"
								value={timeframeFilter}
								onChange={handleTimeframeFilterOptions}
								MenuProps={{
									anchorOrigin: {
										vertical: "bottom",
										horizontal: "left",
									},
									transformOrigin: {
										vertical: "top",
										horizontal: "left",
									},
									getContentAnchorEl: null,
								}}
							>
								{taskTimeframeOptions.map((c: any, i: number) => (
									<MenuItem key={i} value={c}>
										<Typography>{t(c)}</Typography>
									</MenuItem>
								))}
							</Select>
							<Select
								id="filter-status"
								variant="outlined"
								className={`${selectField} ${selectFieldTask}`}
								value={statusFilter}
								onChange={handleStatusFilterOptions}
								MenuProps={{
									anchorOrigin: {
										vertical: "bottom",
										horizontal: "left",
									},
									transformOrigin: {
										vertical: "top",
										horizontal: "left",
									},
									getContentAnchorEl: null,
								}}
							>
								{taskStatusOptions.map((c: any, i: number) => (
									<MenuItem key={i} value={c}>
										<Typography>{t(c)}</Typography>
									</MenuItem>
								))}
							</Select>
						</Box>
					</Box>
					<Divider style={{ margin: "22px 0" }} />
					{!loading && <Tasks userWhere={userFilter} userTasksWhere={taskFilter} />}
				</Box>
			)}
		</div>
	);
};

export default TasksTabPanel;
